import React, { useEffect, useMemo, useState } from 'react';
import { Menu, Tabs, Typography } from 'antd';
import { EnterpriseTokens } from '@/theme/enterprise-tokens';
import {
  EST3_CONTROL_CENTER_NAV,
  EST3_DEFAULT_LEAF,
  findGroupForLeaf,
  type Est3NavGroupKey,
  type Est3NavLeafKey,
} from './epcEstimateControlCenter.nav';
import styles from './EpcEstimateControlCenter.module.css';

const { Text } = Typography;

export interface EpcEstimateControlCenterProps {
  panels: Partial<Record<Est3NavLeafKey, React.ReactNode>>;
  defaultLeaf?: Est3NavLeafKey;
  /** UX3.1 — collapse chrome; full-width workbench (Bid Control Room Estimate tab). */
  focusMode?: boolean;
  /** Jump to a leaf (e.g. from attention chips). */
  requestedLeaf?: Est3NavLeafKey;
  /** UX3.2 — hide left rail; content only */
  immersive?: boolean;
  /** Optional strip below header (status / attention summary). */
  statusStrip?: React.ReactNode;
}

/**
 * EST3 UX2 Wave 1 — left rail primary groups + contextual sub-tabs.
 */
export const EpcEstimateControlCenter: React.FC<EpcEstimateControlCenterProps> = ({
  panels,
  defaultLeaf = EST3_DEFAULT_LEAF,
  statusStrip,
  focusMode,
  requestedLeaf,
  immersive,
}) => {
  const initialGroup = findGroupForLeaf(defaultLeaf)?.key ?? 'scope';
  const [groupKey, setGroupKey] = useState<Est3NavGroupKey>(initialGroup);
  const [leafKey, setLeafKey] = useState<Est3NavLeafKey>(defaultLeaf);

  useEffect(() => {
    if (!requestedLeaf) return;
    const g = findGroupForLeaf(requestedLeaf);
    if (g) setGroupKey(g.key);
    setLeafKey(requestedLeaf);
  }, [requestedLeaf]);

  const group = useMemo(
    () => EST3_CONTROL_CENTER_NAV.find((g) => g.key === groupKey) ?? EST3_CONTROL_CENTER_NAV[0],
    [groupKey]
  );

  const onSelectGroup = (key: string) => {
    const next = EST3_CONTROL_CENTER_NAV.find((g) => g.key === key);
    if (!next) return;
    setGroupKey(next.key);
    const firstAvailable =
      next.leaves.find((l) => panels[l.key] != null)?.key ?? next.leaves[0]?.key;
    if (firstAvailable) setLeafKey(firstAvailable);
  };

  const leafTabs = group.leaves
    .filter((l) => panels[l.key] != null)
    .map((l) => ({
      key: l.key,
      label: l.label,
      children: panels[l.key],
    }));

  const activeLeaf =
    leafTabs.some((t) => t.key === leafKey) ? leafKey : (leafTabs[0]?.key as Est3NavLeafKey | undefined);

  return (
    <div
      className={
        focusMode || immersive
          ? `${styles.root} ${styles.rootFocus}${immersive ? ` ${styles.rootImmersive}` : ''}`
          : styles.root
      }
    >
      {statusStrip && !immersive ? <div className={styles.statusStrip}>{statusStrip}</div> : null}
      <div className={styles.body}>
        {!immersive ? (
        <aside className={styles.rail} aria-label="Estimate Control Center">
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              padding: `0 ${EnterpriseTokens.spacing.md}px ${EnterpriseTokens.spacing.sm}px`,
              display: 'block',
            }}
          >
            Control Center
          </Text>
          <Menu
            mode="inline"
            selectedKeys={[groupKey]}
            onClick={({ key }) => onSelectGroup(String(key))}
            items={EST3_CONTROL_CENTER_NAV.map((g) => ({
              key: g.key,
              label: g.label,
            }))}
            style={{ borderInlineEnd: 'none', background: 'transparent' }}
          />
        </aside>
        ) : null}
        <div className={immersive ? styles.contentImmersive : styles.content}>
          {leafTabs.length === 0 ? (
            <Text type="secondary">No panels available for this section.</Text>
          ) : leafTabs.length === 1 ? (
            leafTabs[0].children
          ) : (
            <Tabs
              activeKey={activeLeaf}
              onChange={(k) => setLeafKey(k as Est3NavLeafKey)}
              items={leafTabs}
            />
          )}
        </div>
      </div>
    </div>
  );
};
